'use client';

import { setupPreviews } from '@previewjs/plugin-react/setup';
import { FunctionComponent } from 'react';

import { VisualButton } from './VisualButton';
import type { IBaseButtonProps } from './IBaseButtonProps';
import type { IButtonStyling } from '../../content/template-data/IButtonStyling';

/**
 * ButtonProps defines the properties that can be passed to the Button component.
 */
export type ButtonProps = IBaseButtonProps & {
  /**
   * Custom Builder styles that can be applied to the button.
   * This allows for additional styling options to be passed in from the CMS.
   */
  styling?: IButtonStyling;
};

/**
 * `Button` is used to create configurable on-brand buttons throughout the site.
 * It uses VisualButton internally to handle styling and rendering.
 */
export const Button: FunctionComponent<ButtonProps> = ({
  id,
  actionId,
  className,
  variant = 'primary',
  submit,
  fullWidth,
  title = '',
  ariaLabel,
  ariaExpanded,
  role,
  size = 'md',
  disabled = false,
  hover = false,
  loading = false,
  onClick,
  onKeyPress,
  style,
  interactionDetails,
  datatestID,
  children,
  form,
  styling,
  ref
}) => {
  const {
    margin,
    padding,
    height,
    width,
    maxWidth,
    minWidth,
    zIndex,
    textTransform,
    fontSize,
    variant: stylingVariant
    // Note: variant is handled separately to allow styling.variant to override the main variant prop if both are provided
  } = styling ?? {};
  const builderStyles: React.CSSProperties = {
    ...(style ?? {}),
    ...(zIndex !== undefined && { zIndex }),
    ...(textTransform !== undefined && { textTransform }),
    ...((margin ?? '') && { margin }),
    ...((padding ?? '') && { padding }),
    ...((height ?? '') && { height }),
    ...((width ?? '') && { width }),
    ...((maxWidth ?? '') && { maxWidth }),
    ...((minWidth ?? '') && { minWidth }),
    ...((fontSize ?? '') && { fontSize })
  };
  return (
    <VisualButton
      id={id}
      actionId={actionId}
      className={className}
      variant={stylingVariant ?? variant}
      fullWidth={fullWidth}
      title={title}
      ariaLabel={ariaLabel}
      ariaExpanded={ariaExpanded}
      role={role}
      size={size}
      disabled={disabled}
      hover={hover}
      loading={loading}
      onClick={onClick}
      onKeyPress={onKeyPress}
      style={builderStyles}
      interactionDetails={interactionDetails}
      datatestID={datatestID}
      submit={submit}
      form={form}
      ref={ref}
    >
      {children}
    </VisualButton>
  );
};

export default Button;

/** @see https://previewjs.com/docs/features/custom-previews */
setupPreviews(Button, () => ({
  default: { children: 'Example Text' } as ButtonProps
}));
